import type { TemplateContent } from "@aswin.dev/types";

function isDomOrHostObject(value: object): boolean {
  if (Array.isArray(value)) {
    return false;
  }
  if (typeof HTMLElement !== "undefined" && value instanceof HTMLElement) {
    return true;
  }
  if (typeof Node !== "undefined" && value instanceof Node) {
    return true;
  }
  if (typeof Event !== "undefined" && value instanceof Event) {
    return true;
  }
  if (
    "nodeType" in value &&
    typeof (value as { nodeType: unknown }).nodeType === "number"
  ) {
    return true;
  }
  const proto = Object.getPrototypeOf(value);
  return proto !== null && proto !== Object.prototype;
}

const SKIPPED_KEYS = new Set([
  "__draggable_context",
  "__draggable_component__",
]);

function shouldSkipKey(key: string): boolean {
  if (SKIPPED_KEYS.has(key)) {
    return true;
  }
  return key.startsWith("Sortable");
}

/**
 * Deep-clones template content for history, auto-save, and duplication.
 * Strips DOM nodes, Sortable instances, and circular references that can leak
 * into editor state from vuedraggable / Sortable.js.
 */
export function cloneTemplateContent(content: TemplateContent): TemplateContent {
  return cloneSerializable(content) as TemplateContent;
}

/** Deep-clones a JSON-like value, stripping DOM nodes and circular refs. */
export function cloneSerializable<T>(value: T): T {
  return cloneValue(value, new WeakMap()) as T;
}

function cloneValue(
  value: unknown,
  seen: WeakMap<object, unknown>,
): unknown {
  if (value === null || typeof value !== "object") {
    return value;
  }

  if (isDomOrHostObject(value)) {
    return undefined;
  }

  if (seen.has(value)) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    seen.set(value, copy);
    for (const item of value) {
      const cloned = cloneValue(item, seen);
      if (cloned !== undefined) {
        copy.push(cloned);
      }
    }
    return copy;
  }

  const copy: Record<string, unknown> = {};
  seen.set(value, copy);
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (shouldSkipKey(key)) {
      continue;
    }
    const cloned = cloneValue(val, seen);
    if (cloned !== undefined) {
      copy[key] = cloned;
    }
  }
  return copy;
}
